import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { includes } from 'lodash-es';
import {
    FormPageTypeEnum,
    PreviewPageType,
    TemplateScopeType,
    TemplateType,
} from '@dev/ee-api/ee-api-models';
import { EditorMultiSignUpEditMessage } from '@dev/template-editor';
import { checkIsUrlInvalid } from '@/services/tools/urlValidation/checkIsUrlInvalid';
import {
    StartLoadingSubmitLandingPages,
    StartLoadingTemplateParams,
    StartLoadingAlreadySubscribedLandingPages,
    getLandingPageList,
    StartLoadingSubmitTemplate,
    StartLoadingTemplateAlreadySubscribed
} from '@/store/landingPageList/actions';
import { 
    getPaidNewsletterCreatorStore,
    TemplateFormPayloadUpdate,
    TemplateFormsCheckInvalid,
    TemplateIncrementAutosaveCounter,
    TemplateSetMultiSignUpEditMessage,
} from '@/store/paidNewsletterCreator/actions';
import { TemplateFormErrorNames } from '@/store/paidNewsletterCreator/types';

import { usePageTypeValue } from './hooks/usePageTypeValue';
import { FormMultiSettingsSubscriptionProps, TemplatesList } from './types';
import { DisplayedScreenEnum, FormPageDropdownSection } from './components/FormPageDropdownSection';
import { FormPageDropdownSectionWrapper } from './styles';
import { FormMultiDoubleOptIn } from './components/FormMultiDoubleOptIn';

export const DEFAULT_OPTIONS_COUNT = 10;
export enum PageTypeEnum {
    DoubleOptIn = 'dpubleOptIn',
    FormSubmit = 'formSubmit',
    AlreadySubscribed = 'alreadySubscribed'
}

const submitPagesParams: StartLoadingTemplateParams = {
    page: 1,
    search: null,
    limit: DEFAULT_OPTIONS_COUNT,
    type: [TemplateScopeType.Personal],
    templateTypes: [TemplateType.LandingPageEditor,TemplateType.LandingPageTemplate],
    isInfinityScroll: true,
    pageType: PageTypeEnum.FormSubmit,
};
const alreadySubscribedPagesParams: StartLoadingTemplateParams = {
    page: 1,
    search: null,
    limit: DEFAULT_OPTIONS_COUNT,
    type: [TemplateScopeType.Personal],
    templateTypes: [TemplateType.LandingPageEditor,TemplateType.LandingPageTemplate],
    isInfinityScroll: true,
    pageType: PageTypeEnum.AlreadySubscribed
};

export const FormMultiSettingsSubscription = ({
    blockClientId,
    selectedBlockClientIdRef
}: FormMultiSettingsSubscriptionProps): JSX.Element => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const { template } = useSelector(getPaidNewsletterCreatorStore);
    const { templateFormSubmit, templateAlreadySubscribed, submitLandingPagesList, alreadySubscribedLandingPagesList } = useSelector(getLandingPageList);
    const clientId = blockClientId ?? selectedBlockClientIdRef.current;
    const templateForm = template.forms?.find((templateForm) => templateForm.blockClientId === clientId);
    const formPayload = templateForm?.formPayload;
    const formObject = templateForm?.form;
    const formSubmitPage = formPayload?.FormSubmitPage ?? formObject?.FormSubmitPage;
    const alreadySubscribedPage = formPayload?.FormAlreadySubscribedPage ?? formObject?.FormAlreadySubscribedPage;
    const [chosenLandingPageSubmitForm, setChosenLandingPageSubmitForm] = useState<TemplatesList>();
    const [landingPageSearchSubmitForm, setLandingPageSearchSubmitForm] = useState(null);
    const [externalWebsiteSubmitForm, setExternalWebsiteSubmitForm] = useState(!checkIsUrlInvalid(formSubmitPage) ? formSubmitPage : null);
    const [isExternalWebsiteErrorSubmitForm, setExternalWebsiteErrorSubmitForm] = useState(false);
    const [landingPageSearchAlreadySubmitedForm, setLandingPageSearchAlreadySubmitedForm] = useState(null);
    const [isExternalWebsiteErrorAlreadySubmitedForm, setExternalWebsiteErrorAlreadySubmitedForm] = useState(false);
    const [externalWebsiteAlreadySubmitedForm, setExternalWebsiteAlreadySubmitedForm] = useState(!checkIsUrlInvalid(alreadySubscribedPage) ? alreadySubscribedPage : null);
    const [chosenLandingPageAlreadySubmitedForm, setChosenLandingPageAlreadySubmitedForm] = useState<TemplatesList>();

    const errors = templateForm?.errors;
    const submitPageLPError = !!errors?.[TemplateFormErrorNames.FORM_SUBMIT_NONE_LP];
    const alreadySubscribedPageLPError = !!errors?.[TemplateFormErrorNames.ALREADY_SUBSCRIBED_NONE_LP];    

    useEffect((): void => {
        !submitLandingPagesList?.length && dispatch(StartLoadingSubmitLandingPages(submitPagesParams));
        !alreadySubscribedLandingPagesList?.length && dispatch(StartLoadingAlreadySubscribedLandingPages(alreadySubscribedPagesParams));
    }, []);

    useEffect((): void => {
        if (
            includes(formPayload?.FormPageType, FormPageTypeEnum.LandingPageFormSubmitPage) &&
            !!formPayload?.FormSubmitPage && templateFormSubmit?.Name !== formPayload?.FormSubmitPage
        ) {
            dispatch(StartLoadingSubmitTemplate(formPayload?.FormSubmitPage));
        }

        if (
            includes(formPayload?.FormPageType, FormPageTypeEnum.LandingPageFormSubscribedPage) &&
            !!formPayload?.FormAlreadySubscribedPage && templateFormSubmit?.Name !== formPayload?.FormAlreadySubscribedPage
        ) {
            dispatch(StartLoadingTemplateAlreadySubscribed(formPayload?.FormAlreadySubscribedPage));
        }
    }, [formPayload?.FormAlreadySubscribedPage, formPayload?.FormSubmitPage]);

    useEffect((): void => {
        setExternalWebsiteErrorSubmitForm(!!errors?.[TemplateFormErrorNames.FORM_SUBMIT_INVALID_URL]);
        setExternalWebsiteErrorAlreadySubmitedForm(!!errors?.[TemplateFormErrorNames.ALREADY_SUBSCRIBED_INVALID_URL]);
    }, [errors]);

    useEffect((): void => {
        setChosenLandingPageSubmitForm({
            label: templateFormSubmit?.Name,
            value: templateFormSubmit?.Name,
            link: templateFormSubmit?.Link
        });
    }, [templateFormSubmit]);
    useEffect((): void => {
        setChosenLandingPageAlreadySubmitedForm({
            label: templateAlreadySubscribed?.Name,
            value: templateAlreadySubscribed?.Name,
            link: templateAlreadySubscribed?.Link,
        });
    }, [templateAlreadySubscribed]);
  

    const changeFormSubmitPage = (ev, obj): void => {
        const formPageType = formPayload?.FormPageType ?? [FormPageTypeEnum.DefaultDoubleOptInPage, FormPageTypeEnum.DefaultFormSubmitPage, FormPageTypeEnum.DefaultFormSubscribedPage];
        const newFormPageType = [...formPageType];
        let newFormSubmitPage = null;

        if (obj.value === DisplayedScreenEnum.Defaultpage) {
            newFormPageType[1] = FormPageTypeEnum.DefaultFormSubmitPage;
        }
        else if (obj.value === DisplayedScreenEnum.Message) {
            newFormPageType[1] = FormPageTypeEnum.ContactUploadSubmitMessage;
        }
        else if (obj.value === DisplayedScreenEnum.Landingpage) {
            newFormPageType[1] = FormPageTypeEnum.LandingPageFormSubmitPage;
            if (submitLandingPagesList.length) {
                setChosenLandingPageSubmitForm({
                    label: submitLandingPagesList[0]?.Name,
                    value: submitLandingPagesList[0]?.Name,
                    link: submitLandingPagesList[0]?.Link,
                });
                newFormSubmitPage = submitLandingPagesList[0]?.Name;
            }
        } else {
            newFormPageType[1] = FormPageTypeEnum.ExternalWebsiteFormSubmitPage;
        }

        setExternalWebsiteSubmitForm(null);
        dispatch(TemplateFormPayloadUpdate(clientId, {FormPageType: newFormPageType, FormSubmitPage: newFormSubmitPage }));
        obj.value !== DisplayedScreenEnum.Externalwebsite && dispatch(TemplateFormsCheckInvalid());
        dispatch(TemplateIncrementAutosaveCounter());
        if (template.multiSignUpEditMessage === EditorMultiSignUpEditMessage.SUCCESS && obj.value !== DisplayedScreenEnum.Message) {
            dispatch(TemplateSetMultiSignUpEditMessage());
        }
    };

    const changeAlreadySubscribedPage = (ev, obj):void => {
        const formPageType = formPayload?.FormPageType ?? [FormPageTypeEnum.DefaultDoubleOptInPage, FormPageTypeEnum.DefaultFormSubmitPage, FormPageTypeEnum.DefaultFormSubscribedPage];
        const newFormPageType = [...formPageType];
        let newAlreadySubscribedPage = null;

        if (obj.value === DisplayedScreenEnum.Defaultpage) {
            newFormPageType[2] = FormPageTypeEnum.DefaultFormSubscribedPage;
        } 
        else if (obj.value === DisplayedScreenEnum.Message) {
            newFormPageType[2] = FormPageTypeEnum.ContactUploadSubscribedMessage;
        }
        else if (obj.value === DisplayedScreenEnum.Landingpage) {
            newFormPageType[2] = FormPageTypeEnum.LandingPageFormSubscribedPage;
            if (alreadySubscribedLandingPagesList.length) {
                setChosenLandingPageAlreadySubmitedForm({
                    label: alreadySubscribedLandingPagesList[0]?.Name,
                    value: alreadySubscribedLandingPagesList[0]?.Name,
                    link: alreadySubscribedLandingPagesList[0]?.Link,
                });
                newAlreadySubscribedPage = alreadySubscribedLandingPagesList[0]?.Name;
            }
            dispatch(TemplateFormsCheckInvalid());
        } else {
            newFormPageType[2] = FormPageTypeEnum.ExternalWebsiteFormSubscribedPage;
        }
        setExternalWebsiteAlreadySubmitedForm(null);
        dispatch(TemplateFormPayloadUpdate(clientId, {FormPageType: newFormPageType, FormAlreadySubscribedPage: newAlreadySubscribedPage }));
        obj.value !== DisplayedScreenEnum.Externalwebsite && dispatch(TemplateFormsCheckInvalid());
        dispatch(TemplateIncrementAutosaveCounter());
        if (template.multiSignUpEditMessage === EditorMultiSignUpEditMessage.FAILURE && obj.value !== DisplayedScreenEnum.Message) {
            dispatch(TemplateSetMultiSignUpEditMessage());
        }
    }
   
    return (
        <>
            <FormMultiDoubleOptIn blockClientId={blockClientId} selectedBlockClientIdRef={selectedBlockClientIdRef} />
            <FormPageDropdownSectionWrapper>
                <FormPageDropdownSection
                    pageTemplateType={PreviewPageType.FormSubmit}
                    idPageDropdown="after-submit"
                    idExternalWebsiteInput="after-submit"
                    label={t('displayscreenaftercontactsubmitform')}
                    onDropdownPageClick={changeFormSubmitPage}
                    pageType={usePageTypeValue(PreviewPageType.FormSubmit, clientId)}
                    externalWebsiteValue={externalWebsiteSubmitForm}
                    setExternalWebsite={setExternalWebsiteSubmitForm}
                    isExternalWebsiteError={isExternalWebsiteErrorSubmitForm}
                    setExternalWebsiteError={setExternalWebsiteErrorSubmitForm}
                    idLandingPage="after-submit"
                    chosenLandingPage={chosenLandingPageSubmitForm}
                    setChosenLandingPage={setChosenLandingPageSubmitForm}
                    landingPageSearch={landingPageSearchSubmitForm}
                    setLandingPageSearch={setLandingPageSearchSubmitForm}
                    landingPagesParams={submitPagesParams}
                    clientId={clientId}
                    lpError={submitPageLPError}
                    isPreviewOpen={template.multiSignUpEditMessage === EditorMultiSignUpEditMessage.SUCCESS}
                />
            </FormPageDropdownSectionWrapper>
            <FormPageDropdownSectionWrapper>
                <FormPageDropdownSection
                    pageTemplateType={PreviewPageType.AlreadySubscribed}
                    idPageDropdown="already-subscribed"
                    idExternalWebsiteInput="already-subscribed"
                    label={t('displayscreenafterhaspreviouslysubscribed')}
                    onDropdownPageClick={changeAlreadySubscribedPage}
                    pageType={usePageTypeValue(PreviewPageType.AlreadySubscribed, clientId)}
                    externalWebsiteValue={externalWebsiteAlreadySubmitedForm}
                    setExternalWebsite={setExternalWebsiteAlreadySubmitedForm}
                    isExternalWebsiteError={isExternalWebsiteErrorAlreadySubmitedForm}
                    setExternalWebsiteError={setExternalWebsiteErrorAlreadySubmitedForm}
                    idLandingPage="already-subscribed"
                    chosenLandingPage={chosenLandingPageAlreadySubmitedForm}
                    setChosenLandingPage={setChosenLandingPageAlreadySubmitedForm}
                    landingPageSearch={landingPageSearchAlreadySubmitedForm}
                    setLandingPageSearch={setLandingPageSearchAlreadySubmitedForm}
                    landingPagesParams={alreadySubscribedPagesParams}
                    clientId={clientId}
                    lpError={alreadySubscribedPageLPError}
                    isPreviewOpen={template.multiSignUpEditMessage === EditorMultiSignUpEditMessage.FAILURE}
                />
            </FormPageDropdownSectionWrapper>
        </>
    )
}